import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  FileCheck, 
  Calendar as CalendarIcon,
  CheckCircle2,
  Circle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Trophy,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Loader2,
  Tag,
  MessageSquare,
  Filter,
  ChevronDown,
  AlertCircle,
  Flame,
  Clock,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Receipt,
  Zap,
  CheckCircle
} from "lucide-react";
import { BillsReviewInterstitial } from "../components/reconcile/BillsReviewInterstitial";
import { format, subDays, startOfWeek, endOfWeek, differenceInDays } from "date-fns";
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Step indicator component
function StepIndicator({ currentStep, totalSteps }) {
  const steps = [
    { num: 1, name: "Select Range" },
    { num: 2, name: "Review" },
    { num: 3, name: "Resolve" },
    { num: 4, name: "Confirm" },
    { num: 5, name: "Complete" }
  ];

  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={step.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
              currentStep > step.num 
                ? 'bg-emerald-500 text-white' 
                : currentStep === step.num 
                  ? 'bg-emerald-600 text-white ring-4 ring-emerald-100 dark:ring-emerald-900/50' 
                  : 'bg-muted text-muted-foreground'
            }`}>
              {currentStep > step.num ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                step.num
              )}
            </div>
            <span className={`text-xs mt-2 hidden sm:block ${
              currentStep >= step.num ? 'text-foreground' : 'text-muted-foreground'
            }`}>
              {step.name}
            </span>
          </div>
          
          {index < steps.length - 1 && (
            <div className={`w-12 sm:w-24 h-1 mx-2 rounded ${
              currentStep > step.num ? 'bg-emerald-500' : 'bg-muted'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

// XP Toast component
function XPToast({ xp, streak }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
        <Sparkles className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="font-heading font-bold text-lg">+{xp} XP Earned!</p>
        {streak > 0 && (
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Flame className="w-4 h-4 text-orange-500" />
            {streak} day streak bonus included
          </p>
        )}
      </div>
    </div>
  );
}

export function ReconcileContent() {
  return <ReconcileInner embedded />;
}

export default function ReconcilePage() {
  return <ReconcileInner />;
}

function ReconcileInner({ embedded = false }) {
  const { household, accounts } = useAuth();
  const Wrap = embedded ? ({children}) => <>{children}</> : AppLayout;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0 = overview, 1-5 = wizard steps
  const [showFirstTimeIntro, setShowFirstTimeIntro] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  
  // Step 1: Date range and accounts
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 7),
    end: new Date()
  });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  
  // Step 2-3: Transactions
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedTx, setSelectedTx] = useState(new Set());
  
  // Step 4: Summary
  const [summary, setSummary] = useState(null);
  
  // Completion
  const [completing, setCompleting] = useState(false);
  const [completionResult, setCompletionResult] = useState(null);
  
  // Bills Review Interstitial
  const [showBillsInterstitial, setShowBillsInterstitial] = useState(false);
  const [pendingBillsData, setPendingBillsData] = useState(null);
  const [checkingPendingBills, setCheckingPendingBills] = useState(false);

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  // Initialize selectedAccounts from context accounts
  useEffect(() => {
    if (accounts.length > 0 && selectedAccounts.length === 0) {
      setSelectedAccounts(accounts.map(a => a.account_id));
    }
  }, [accounts]);

  const fetchData = async () => {
    try {
      const [sessRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/reconcile`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' })
      ]);

      if (sessRes.ok) {
        const sessionData = await sessRes.json();
        setSessions(sessionData);
        // Check if first time (no completed sessions)
        const hasCompleted = sessionData.some(s => s.status === 'completed');
        if (!hasCompleted) {
          setShowFirstTimeIntro(true);
        }
      }
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startReconciliation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/reconcile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          period_start: dateRange.start.toISOString(),
          period_end: dateRange.end.toISOString()
        })
      });

      if (!response.ok) throw new Error('Failed to start reconciliation');

      const data = await response.json();
      setActiveSession(data);
      
      // Fetch transactions for this period
      await fetchTransactions();
      
      setStep(2);
      toast.success("Reconciliation started");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchTransactions = async () => {
    try {
      const startStr = dateRange.start.toISOString().split('T')[0];
      const endStr = dateRange.end.toISOString().split('T')[0];
      
      const response = await fetch(
        `${API_URL}/api/households/${household.household_id}/transactions?start_date=${startStr}&end_date=${endStr}&limit=500`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        let txs = data.transactions || [];
        
        // Filter by selected accounts
        if (selectedAccounts.length > 0) {
          txs = txs.filter(tx => selectedAccounts.includes(tx.account_id));
        }
        
        setTransactions(txs);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const updateTransaction = async (txId, updates) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/transactions/${txId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      // Update local state
      setTransactions(prev => prev.map(tx => 
        tx.transaction_id === txId ? { ...tx, ...updates } : tx
      ));
    } catch (error) {
      console.error('Transaction update error:', error);
      toast.error('Could not update transaction. Please try again.');
    }
  };

  const bulkUpdateTransactions = async (txIds, updates) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/transactions/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transaction_ids: txIds,
          ...updates
        })
      });
      
      // Update local state
      setTransactions(prev => prev.map(tx => 
        txIds.includes(tx.transaction_id) ? { ...tx, ...updates } : tx
      ));
      
      setSelectedTx(new Set());
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Could not update transactions. Please try again.');
    }
  };

  const markTransferPair = async (tx1Id, tx2Id) => {
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/transactions/mark-transfer-pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ transaction_ids: [tx1Id, tx2Id] })
      });
      
      // Update local state
      const transferCat = categories.find(c => c.name === 'Transfer');
      setTransactions(prev => prev.map(tx => 
        tx.transaction_id === tx1Id || tx.transaction_id === tx2Id
          ? { ...tx, is_transfer: true, category_id: transferCat?.category_id, status: 'reviewed' }
          : tx
      ));
      
      toast.success("Marked as transfer");
    } catch (error) {
      console.error('Mark transfer error:', error);
      toast.error('Could not mark as transfer. Please try again.');
    }
  };

  const calculateSummary = () => {
    const catMap = {};
    categories.forEach(c => { catMap[c.category_id] = c; });
    
    const byCategory = {};
    let totalIncome = 0;
    let totalExpenses = 0;
    let transferCount = 0;
    let uncategorizedCount = 0;
    
    transactions.forEach(tx => {
      if (tx.is_transfer) {
        transferCount++;
        return;
      }
      
      if (!tx.category_id) {
        uncategorizedCount++;
        return;
      }
      
      const cat = catMap[tx.category_id];
      const catName = cat?.name || 'Other';
      const catColor = cat?.color || '#6B7280';
      
      if (!byCategory[catName]) {
        byCategory[catName] = { name: catName, color: catColor, total: 0, count: 0 };
      }
      byCategory[catName].total += tx.amount;
      byCategory[catName].count++;
      
      if (tx.amount > 0) {
        totalIncome += tx.amount;
      } else {
        totalExpenses += Math.abs(tx.amount);
      }
    });
    
    // Find bills paid (transactions matching bill names or in fixed categories)
    const billsPaid = transactions.filter(tx => {
      const cat = catMap[tx.category_id];
      return cat?.group === 'fixed' && tx.amount < 0;
    });
    
    setSummary({
      byCategory: Object.values(byCategory).sort((a, b) => Math.abs(b.total) - Math.abs(a.total)),
      totalIncome,
      totalExpenses,
      netCashflow: totalIncome - totalExpenses,
      transferCount: Math.floor(transferCount / 2), // Pairs
      uncategorizedCount,
      billsPaid,
      totalTransactions: transactions.length
    });
  };

  // Check for pending bills before completion
  const checkPendingBillsAndComplete = async () => {
    if (!activeSession) return;
    
    setCheckingPendingBills(true);
    try {
      const response = await fetch(
        `${API_URL}/api/households/${household.household_id}/reconcile/${activeSession.session_id}/pending-bills`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        // If endpoint fails, proceed with completion
        await completeReconciliation();
        return;
      }
      
      const data = await response.json();
      
      // Track pending bills count metric
      if (data.total_pending > 0) {
        try {
          await fetch(`${API_URL}/api/beta/metrics/track`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              metric_name: 'weekly_close_bills_pending_count',
              value: data.total_pending,
              metadata: { 
                draft_bills: data.draft_bills_count,
                needs_review: data.needs_review_count
              }
            })
          });
        } catch (e) {
          console.error('Failed to track metric:', e);
        }
      }
      
      if (data.has_pending) {
        // Show interstitial
        setPendingBillsData({
          draftBills: data.draft_bills || [],
          needsReviewImports: data.needs_review_imports || [],
          count: data.total_pending
        });
        setShowBillsInterstitial(true);
      } else {
        // No pending bills, proceed directly
        await completeReconciliation();
      }
    } catch (error) {
      console.error('Error checking pending bills:', error);
      // On error, proceed with completion
      await completeReconciliation();
    } finally {
      setCheckingPendingBills(false);
    }
  };

  const completeReconciliation = async () => {
    if (!activeSession) return;
    
    setShowBillsInterstitial(false);
    setCompleting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/households/${household.household_id}/reconcile/${activeSession.session_id}/complete`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Failed to complete');

      const data = await response.json();
      setCompletionResult(data);
      setStep(5);
      
      // Show XP toast (keeping for now, can be removed later for full ARC alignment)
      toast.custom((t) => (
        <XPToast xp={data.xp_earned} streak={0} />
      ), {
        duration: 5000,
      });
      
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCompleting(false);
    }
  };

  // Handle interstitial actions
  const handleReviewBills = () => {
    setShowBillsInterstitial(false);
    // Navigate to bills page
    navigate('/bills');
  };

  const handleContinueWithoutReview = () => {
    // Close interstitial and proceed with completion
    completeReconciliation();
  };

  const resetWizard = () => {
    setStep(0);
    setActiveSession(null);
    setTransactions([]);
    setSummary(null);
    setCompletionResult(null);
    setSelectedTx(new Set());
    setPendingBillsData(null);
    setDateRange({
      start: subDays(new Date(), 7),
      end: new Date()
    });
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.category_id === catId);
    return cat?.name || 'Uncategorized';
  };

  // Filter transactions
  const getFilteredTransactions = () => {
    switch (filter) {
      case 'uncategorized':
        return transactions.filter(tx => !tx.category_id && !tx.is_transfer);
      case 'flagged':
        return transactions.filter(tx => tx.status === 'imported' && !tx.is_transfer);
      case 'transfers':
        return transactions.filter(tx => tx.is_transfer);
      default:
        return transactions;
    }
  };

  // Detect potential transfers
  const getPotentialTransfers = () => {
    const pairs = [];
    const byAmount = {};
    
    transactions.forEach(tx => {
      if (tx.is_transfer) return;
      const absAmount = Math.abs(tx.amount).toFixed(2);
      if (!byAmount[absAmount]) byAmount[absAmount] = [];
      byAmount[absAmount].push(tx);
    });
    
    Object.values(byAmount).forEach(group => {
      if (group.length < 2) return;
      const positives = group.filter(t => t.amount > 0);
      const negatives = group.filter(t => t.amount < 0);
      
      positives.forEach(pos => {
        negatives.forEach(neg => {
          if (pos.account_id !== neg.account_id) {
            const daysDiff = Math.abs(differenceInDays(new Date(pos.date), new Date(neg.date)));
            if (daysDiff <= 3) {
              pairs.push({ positive: pos, negative: neg, daysDiff });
            }
          }
        });
      });
    });
    
    return pairs;
  };

  const filteredTx = getFilteredTransactions();
  const potentialTransfers = getPotentialTransfers();
  const unreconciledCount = transactions.filter(tx => tx.status !== 'reconciled').length;
  const uncategorizedCount = transactions.filter(tx => !tx.category_id && !tx.is_transfer).length;
  const progressPercent = transactions.length > 0 
    ? Math.round(((transactions.length - uncategorizedCount) / transactions.length) * 100)
    : 100;

  const COLORS = ['#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-[#1a4d3e] border-t-transparent rounded-full animate-spin" />
        </div>
      </Wrap>
    );
  }

  // First-time onboarding clarifier
  if (showFirstTimeIntro && step === 0) {
    return (
      <Wrap>
        <div className="flex items-center justify-center min-h-[calc(100vh-8rem)]" data-testid="first-time-intro">
          <div className="max-w-md text-center px-6">
            <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-6">
              <FileCheck className="w-7 h-7 text-[#1a4d3e]" />
            </div>
            
            <h1 className="font-heading text-2xl font-semibold text-[#1a4d3e] mb-3">
              This is your review & reset.
            </h1>
            
            <p className="text-[#1a4d3e]/60 mb-8">
              ARA Wealth isn't about tracking every dollar. It's about closing the week with clarity.
            </p>
            
            <div className="bg-[#faf9f6] rounded-xl p-6 mb-8 text-left">
              <ul className="space-y-3 text-sm text-[#1a4d3e]/70">
                <li className="flex items-start gap-3">
                  <span className="text-[#1a4d3e] mt-0.5">•</span>
                  <span>See what moved</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#1a4d3e] mt-0.5">•</span>
                  <span>Confirm alignment</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#1a4d3e] mt-0.5">•</span>
                  <span>Start fresh</span>
                </li>
              </ul>
            </div>
            
            <Button 
              className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white px-8"
              onClick={() => {
                setShowFirstTimeIntro(false);
                setStep(1);
              }}
              data-testid="begin-weekly-close-btn"
            >
              Begin your reset
            </Button>
          </div>
        </div>
      </Wrap>
    );
  }

  // Overview page (step 0)

  // Overview (step 0)
  if (step === 0) {
    return (
      <Wrap>
        <div className="p-6 lg:p-8" data-testid="reconcile-page">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl font-semibold text-[#1a4d3e]">Confirm your activity</h1>
              <p className="text-[#1a4d3e]/60 text-sm">Confirm your transactions so everything is clear and accurate</p>
            </div>
            
            <Button 
              className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white" 
              onClick={() => setStep(1)} 
              data-testid="start-reconcile-btn"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              Begin your reset
            </Button>
          </div>

          {/* Simple Stats - no gamification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-[#1a4d3e]/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#1a4d3e]" />
                </div>
                <span className="text-[#1a4d3e]/60 text-sm">Completed</span>
              </div>
              <p className="font-heading text-2xl font-semibold text-[#1a4d3e]">
                {sessions.filter(s => s.status === 'completed').length}
              </p>
              <p className="text-xs text-[#1a4d3e]/40">resets</p>
            </div>
            
            <div className="bg-white rounded-xl border border-[#1a4d3e]/10 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-[#1a4d3e]/60 text-sm">Current streak</span>
              </div>
              <p className="font-heading text-2xl font-semibold text-[#1a4d3e]">
                {sessions.filter(s => s.status === 'completed').length > 0 ? '1' : '0'}
              </p>
              <p className="text-xs text-[#1a4d3e]/40">consecutive weeks</p>
            </div>
          </div>

          {/* Past Sessions */}
          <div className="bg-white rounded-xl border border-[#1a4d3e]/10 p-6">
            <h3 className="font-heading text-sm font-medium text-[#1a4d3e] mb-4">Recent resets</h3>
            
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <FileCheck className="w-10 h-10 text-[#1a4d3e]/20 mx-auto mb-3" />
                <p className="text-[#1a4d3e]/50 text-sm">Nothing here yet</p>
                <p className="text-xs text-[#1a4d3e]/40 mt-1">Start your first reset above</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.slice(0, 10).map((session) => (
                  <div key={session.session_id} className="flex items-center justify-between py-3 border-b border-[#1a4d3e]/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        session.status === 'completed' 
                          ? 'bg-[#1a4d3e]/10' 
                          : 'bg-amber-50'
                      }`}>
                        {session.status === 'completed' ? (
                          <CheckCircle2 className="w-4 h-4 text-[#1a4d3e]" />
                        ) : (
                          <Circle className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1a4d3e]">
                          {format(new Date(session.period_start), 'MMM d')} – {format(new Date(session.period_end), 'MMM d, yyyy')}
                        </p>
                        <p className="text-xs text-[#1a4d3e]/40">
                          {session.status === 'completed' ? 'Closed' : 'In progress'}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-[#1a4d3e]/50">
                      {session.transactions_count || 0} transactions
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Wrap>
    );
  }

  // Wizard steps 1-5
  return (
    <Wrap>
      <div className="p-6 lg:p-8" data-testid="reconcile-wizard">
        {/* Progress indicator */}
        <StepIndicator currentStep={step} totalSteps={5} />

        {/* Step 1: Select Date Range & Accounts */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto animate-fade-in" data-testid="step-1">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">Select Period</h2>
              <p className="text-muted-foreground">Choose the date range and accounts to reconcile</p>
            </div>

            <div className="card-dashboard space-y-6">
              {/* Date Range */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Date Range</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={format(dateRange.start, 'yyyy-MM-dd')}
                      onChange={(e) => setDateRange({ ...dateRange, start: new Date(e.target.value) })}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={format(dateRange.end, 'yyyy-MM-dd')}
                      onChange={(e) => setDateRange({ ...dateRange, end: new Date(e.target.value) })}
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                
                {/* Quick select buttons */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Last 7 days', days: 7 },
                    { label: 'Last 14 days', days: 14 },
                    { label: 'This month', days: 30 },
                  ].map(({ label, days }) => (
                    <Button
                      key={days}
                      variant="outline"
                      size="sm"
                      onClick={() => setDateRange({
                        start: subDays(new Date(), days),
                        end: new Date()
                      })}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Account Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Accounts</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedAccounts(
                      selectedAccounts.length === accounts.length ? [] : accounts.map(a => a.account_id)
                    )}
                  >
                    {selectedAccounts.length === accounts.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accounts.map(account => (
                    <label
                      key={account.account_id}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedAccounts.includes(account.account_id)
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-border hover:bg-muted/30'
                      }`}
                    >
                      <Checkbox
                        checked={selectedAccounts.includes(account.account_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAccounts([...selectedAccounts, account.account_id]);
                          } else {
                            setSelectedAccounts(selectedAccounts.filter(id => id !== account.account_id));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{account.display_name || account.name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{account.account_type}</p>
                      </div>
                      <span className={`font-medium tabular-nums ${account.balance < 0 ? 'text-destructive' : ''}`}>
                        {formatCurrency(account.balance)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t border-border">
                <Button variant="outline" onClick={resetWizard}>
                  Cancel
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={startReconciliation}
                  disabled={selectedAccounts.length === 0}
                  data-testid="continue-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Review Transactions */}
        {step === 2 && (
          <div className="animate-fade-in" data-testid="step-2">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-2xl font-bold">Review Transactions</h2>
                <p className="text-muted-foreground">
                  {transactions.length} transactions in this period
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Progress */}
                <div className="hidden sm:flex items-center gap-3">
                  <div className="w-32">
                    <Progress value={progressPercent} className="h-2" />
                  </div>
                  <span className="text-sm text-muted-foreground">{progressPercent}%</span>
                </div>
                
                {/* Filter */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Filter className="w-4 h-4 mr-2" />
                      {filter === 'all' ? 'All' : filter === 'uncategorized' ? 'Uncategorized' : filter === 'flagged' ? 'Needs Review' : 'Transfers'}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setFilter('all')}>
                      All ({transactions.length})
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFilter('uncategorized')}>
                      <AlertCircle className="w-4 h-4 mr-2 text-amber-500" />
                      Uncategorized ({uncategorizedCount})
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFilter('flagged')}>
                      <Tag className="w-4 h-4 mr-2 text-blue-500" />
                      Needs Review ({transactions.filter(tx => tx.status === 'imported' && !tx.is_transfer).length})
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFilter('transfers')}>
                      <ArrowLeftRight className="w-4 h-4 mr-2 text-emerald-500" />
                      Transfers ({transactions.filter(tx => tx.is_transfer).length})
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Potential Transfers Alert */}
            {potentialTransfers.length > 0 && filter !== 'transfers' && (
              <div className="mb-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50">
                <div className="flex items-center gap-3">
                  <ArrowLeftRight className="w-5 h-5 text-emerald-600" />
                  <div className="flex-1">
                    <p className="font-medium text-emerald-800 dark:text-emerald-200">
                      {potentialTransfers.length} potential transfer pair{potentialTransfers.length > 1 ? 's' : ''} detected
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      Review and confirm in the next step
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setStep(3)}>
                    Review
                  </Button>
                </div>
              </div>
            )}

            {/* Transactions List */}
            <div className="card-dashboard">
              {/* Bulk actions */}
              {selectedTx.size > 0 && (
                <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50">
                  <span className="font-medium">{selectedTx.size} selected</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm">
                        Bulk Actions
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {categories.slice(0, 10).map(cat => (
                        <DropdownMenuItem 
                          key={cat.category_id}
                          onClick={() => bulkUpdateTransactions(Array.from(selectedTx), { category_id: cat.category_id, status: 'reviewed' })}
                        >
                          Set: {cat.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTx(new Set())}>
                    Clear
                  </Button>
                </div>
              )}

              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {filteredTx.map((tx) => (
                  <div 
                    key={tx.transaction_id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                      selectedTx.has(tx.transaction_id)
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-transparent hover:bg-muted/30'
                    }`}
                  >
                    <Checkbox
                      checked={selectedTx.has(tx.transaction_id)}
                      onCheckedChange={(checked) => {
                        const newSet = new Set(selectedTx);
                        if (checked) newSet.add(tx.transaction_id);
                        else newSet.delete(tx.transaction_id);
                        setSelectedTx(newSet);
                      }}
                    />
                    
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      tx.amount > 0 
                        ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                        : 'bg-stone-100 dark:bg-stone-800/30'
                    }`}>
                      {tx.is_transfer ? (
                        <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                      ) : tx.amount > 0 ? (
                        <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-stone-600 dark:text-stone-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{tx.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(tx.date), 'MMM d')}
                        {tx.category_id && ` • ${getCategoryName(tx.category_id)}`}
                      </p>
                    </div>

                    <span className={`font-medium tabular-nums ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                      {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                    </span>

                    {/* Quick categorize */}
                    {!tx.category_id && !tx.is_transfer && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Tag className="w-4 h-4 mr-1" />
                            Categorize
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="max-h-64 overflow-y-auto">
                          {categories.map(cat => (
                            <DropdownMenuItem 
                              key={cat.category_id}
                              onClick={() => updateTransaction(tx.transaction_id, { category_id: cat.category_id, status: 'reviewed' })}
                            >
                              {cat.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                className="btn-primary"
                onClick={() => setStep(3)}
                data-testid="continue-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Resolve Issues */}
        {step === 3 && (
          <div className="animate-fade-in" data-testid="step-3">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold mb-2">Resolve Issues</h2>
              <p className="text-muted-foreground">Review potential transfers and uncategorized transactions</p>
            </div>

            <Tabs defaultValue="transfers" className="space-y-6">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="transfers">
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Transfers ({potentialTransfers.length})
                </TabsTrigger>
                <TabsTrigger value="uncategorized">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Uncategorized ({uncategorizedCount})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="transfers" className="space-y-4">
                {potentialTransfers.length === 0 ? (
                  <div className="card-dashboard text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h3 className="font-heading font-semibold mb-2">No Transfers Found</h3>
                    <p className="text-sm text-muted-foreground">We didn't spot any transfers this time</p>
                  </div>
                ) : (
                  potentialTransfers.map((pair, index) => (
                    <div key={index} className="card-dashboard">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-5 h-5 text-emerald-500" />
                          <span className="font-medium">Potential Transfer</span>
                          <Badge variant="secondary">{pair.daysDiff} days apart</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            Not a Transfer
                          </Button>
                          <Button 
                            size="sm"
                            className="btn-primary"
                            onClick={() => markTransferPair(pair.positive.transaction_id, pair.negative.transaction_id)}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Confirm
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {[pair.negative, pair.positive].map((tx) => (
                          <div key={tx.transaction_id} className={`p-4 rounded-lg border ${
                            tx.amount > 0 
                              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50' 
                              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              {tx.amount > 0 ? (
                                <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <ArrowUpRight className="w-4 h-4 text-red-600" />
                              )}
                              <span className="text-sm font-medium">
                                {tx.amount > 0 ? 'Money In' : 'Money Out'}
                              </span>
                            </div>
                            <p className="font-medium mb-1 truncate">{tx.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(tx.date), 'MMM d, yyyy')}
                            </p>
                            <p className={`font-heading text-xl font-bold mt-2 ${
                              tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="uncategorized" className="space-y-2">
                {uncategorizedCount === 0 ? (
                  <div className="card-dashboard text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <h3 className="font-heading font-semibold mb-2">All Categorized</h3>
                    <p className="text-sm text-muted-foreground">Every transaction has a category</p>
                  </div>
                ) : (
                  <div className="card-dashboard">
                    {transactions.filter(tx => !tx.category_id && !tx.is_transfer).map((tx) => (
                      <div 
                        key={tx.transaction_id}
                        className="flex items-center gap-4 p-4 border-b border-border last:border-0"
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          tx.amount > 0 ? 'bg-emerald-100' : 'bg-stone-100'
                        }`}>
                          {tx.amount > 0 ? (
                            <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-stone-600" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <p className="font-medium">{tx.description}</p>
                          <p className="text-sm text-muted-foreground">{format(new Date(tx.date), 'MMM d')}</p>
                        </div>

                        <span className={`font-medium tabular-nums ${tx.amount > 0 ? 'text-emerald-600' : ''}`}>
                          {formatCurrency(tx.amount)}
                        </span>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Tag className="w-4 h-4 mr-1" />
                              Categorize
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="max-h-64 overflow-y-auto">
                            {categories.map(cat => (
                              <DropdownMenuItem 
                                key={cat.category_id}
                                onClick={() => updateTransaction(tx.transaction_id, { category_id: cat.category_id, status: 'reviewed' })}
                              >
                                {cat.name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                className="btn-primary"
                onClick={() => { calculateSummary(); setStep(4); }}
                data-testid="continue-btn"
              >
                Continue to Summary
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && summary && (
          <div className="animate-fade-in" data-testid="step-4">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-semibold text-[#1a4d3e] mb-2">Confirm your reset</h2>
              <p className="text-[#1a4d3e]/60">Review the summary before completing</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Cashflow Summary */}
              <div className="bg-white rounded-xl border border-[#1a4d3e]/10 p-6">
                <h3 className="font-heading text-sm font-medium text-[#1a4d3e] mb-4">Period Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1a4d3e]/5">
                    <div className="flex items-center gap-3">
                      <ArrowDownRight className="w-4 h-4 text-[#1a4d3e]" />
                      <span className="text-sm text-[#1a4d3e]/70">Total Income</span>
                    </div>
                    <span className="font-heading font-semibold text-[#1a4d3e]">
                      +{formatCurrency(summary.totalIncome)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1a4d3e]/5">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="w-4 h-4 text-[#1a4d3e]/60" />
                      <span className="text-sm text-[#1a4d3e]/70">Total Expenses</span>
                    </div>
                    <span className="font-heading font-semibold text-[#1a4d3e]/70">
                      -{formatCurrency(summary.totalExpenses)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border-2 border-[#1a4d3e]/20 bg-[#faf9f6]">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-4 h-4 text-[#1a4d3e]" />
                      <span className="text-sm font-medium text-[#1a4d3e]">Net Movement</span>
                    </div>
                    <span className={`font-heading text-lg font-semibold ${
                      summary.netCashflow >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'
                    }`}>
                      {summary.netCashflow >= 0 ? '+' : ''}{formatCurrency(summary.netCashflow)}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-[#1a4d3e]/10">
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold">{summary.totalTransactions}</p>
                    <p className="text-xs text-muted-foreground">Transactions</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold">{summary.transferCount}</p>
                    <p className="text-xs text-muted-foreground">Transfers</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold">{summary.billsPaid.length}</p>
                    <p className="text-xs text-muted-foreground">Bills Paid</p>
                  </div>
                </div>
              </div>

              {/* Spending by Category */}
              <div className="card-dashboard">
                <h3 className="font-heading font-semibold mb-4">Spending by Category</h3>
                
                {summary.byCategory.filter(c => c.total < 0).length > 0 ? (
                  <>
                    <div className="h-48 mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={summary.byCategory.filter(c => c.total < 0).map(c => ({ ...c, value: Math.abs(c.total) }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            innerRadius={40}
                            dataKey="value"
                            nameKey="name"
                          >
                            {summary.byCategory.filter(c => c.total < 0).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload?.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-card px-3 py-2 rounded-lg shadow-lg border border-border">
                                    <p className="font-medium">{data.name}</p>
                                    <p className="text-sm">{formatCurrency(data.value)}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {summary.byCategory.filter(c => c.total < 0).slice(0, 8).map((cat, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: cat.color || COLORS[index % COLORS.length] }}
                            />
                            <span>{cat.name}</span>
                          </div>
                          <span className="font-medium tabular-nums">{formatCurrency(Math.abs(cat.total))}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-48 flex items-center justify-center text-muted-foreground">
                    No spending categories in this period
                  </div>
                )}
              </div>
            </div>

            {/* Bills Paid */}
            {summary.billsPaid.length > 0 && (
              <div className="card-dashboard mb-6">
                <h3 className="font-heading font-semibold mb-4">Bills Paid This Period</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {summary.billsPaid.slice(0, 8).map((tx) => (
                    <div key={tx.transaction_id} className="p-3 rounded-lg bg-muted/30">
                      <p className="font-medium truncate">{tx.description}</p>
                      <p className="text-sm text-muted-foreground">{format(new Date(tx.date), 'MMM d')}</p>
                      <p className="font-medium mt-1">{formatCurrency(Math.abs(tx.amount))}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning for uncategorized - neutral tone */}
            {summary.uncategorizedCount > 0 && (
              <div className="mb-6 p-4 rounded-xl bg-[#faf9f6] border border-[#1a4d3e]/10">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#1a4d3e]/50" />
                  <div>
                    <p className="font-medium text-[#1a4d3e]">
                      {summary.uncategorizedCount} uncategorized transaction{summary.uncategorizedCount > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-[#1a4d3e]/60">
                      You can still reset the week, or go back to categorize them first
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions - no XP preview, calm button */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)} className="border-[#1a4d3e]/20">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white px-8"
                onClick={checkPendingBillsAndComplete}
                disabled={completing || checkingPendingBills}
                data-testid="complete-btn"
              >
                {(completing || checkingPendingBills) ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    Finish reset
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Completion - Calm, not gamified */}
        {step === 5 && completionResult && (
          <div className="animate-fade-in text-center" data-testid="step-5">
            <div className="max-w-md mx-auto">
              {/* Calm completion - no confetti, no gamification */}
              <div className="w-16 h-16 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#1a4d3e]" />
              </div>
              
              <h2 className="font-heading text-2xl font-semibold text-[#1a4d3e] mb-2">You're set.</h2>
              <p className="text-[#1a4d3e]/60 mb-8">
                Everything is in place for your next cycle.
              </p>

              {/* Simple summary - no flashy stats */}
              <div className="bg-[#faf9f6] rounded-xl p-6 mb-8 text-left">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#1a4d3e]/60">Transactions reviewed</span>
                    <span className="text-[#1a4d3e] font-medium">{completionResult.transactions_reconciled}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#1a4d3e]/60">Period</span>
                    <span className="text-[#1a4d3e] font-medium">
                      {differenceInDays(dateRange.end, dateRange.start)} days
                    </span>
                  </div>
                </div>
                
                {/* Bills clarity summary line */}
                <div className="mt-4 pt-4 border-t border-[#1a4d3e]/10">
                  <p className="text-sm text-[#1a4d3e]/70 text-center">
                    All known bills are accounted for. You're entering the week with clarity.
                  </p>
                </div>
              </div>

              <Button 
                className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white px-8"
                onClick={resetWizard}
                data-testid="close-complete-btn"
              >
                Done
              </Button>
              
              <p className="text-xs text-[#1a4d3e]/40 mt-6">
                Closing the week protects momentum.
              </p>
            </div>
          </div>
        )}
        
        {/* Bills Review Interstitial Modal */}
        <BillsReviewInterstitial
          isOpen={showBillsInterstitial}
          onClose={() => setShowBillsInterstitial(false)}
          onReviewBills={handleReviewBills}
          onContinueWithoutReview={handleContinueWithoutReview}
          pendingBills={pendingBillsData}
          householdId={household?.household_id}
        />
      </div>
    </Wrap>
  );
}
